import { useState, useCallback } from 'react';
import { apiRawFetch } from '../../main/client';

export interface TaxPayment {
    id: number;
    taxPeriodId: number;
    propertyId: number;
    componentId: string;
    amount: number;
    currency: string;
    paidDate: string;
    reference: string | null;
    notes: string | null;
    created: string;
    updated: string;
}

export interface CreatePaymentData {
    taxPeriodId: number;
    amount: number;
    currency: string;
    paidDate: string;
    reference?: string;
    notes?: string;
}

export default function useTaxPayments(propertyId: number | undefined) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createPayment = useCallback(async (data: CreatePaymentData): Promise<TaxPayment> => {
        if (!propertyId) {
            throw new Error('Property ID is required');
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/tax-payments`,
                'POST',
                data
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create payment');
            }

            const { data: payment } = await response.json();
            return payment;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create payment';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    const deletePayment = useCallback(async (paymentId: number): Promise<void> => {
        if (!propertyId) {
            throw new Error('Property ID is required');
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/tax-payments/${paymentId}`,
                'DELETE'
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete payment');
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete payment';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    return {
        loading,
        error,
        createPayment,
        deletePayment,
    };
}
